import { useEffect, useState } from 'react';
import api from '../services/api';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import Table, { Column } from './Table';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';

export interface MasterField {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'checkbox' | 'select' | 'textarea';
  options?: { value: string; label: string }[];
  maxLength?: number;
  required?: boolean;
}

export interface MasterCrudConfig {
  title: string;
  apiBase: string; // e.g. '/payroll/masters/races'
  listKey: string; // e.g. 'races'
  entityKey: string; // e.g. 'race'
  fields: MasterField[]; // excludes code/name, which are always first
  hasCodeName?: boolean; // default true
  // ADM-29: when embedding this page as a tab panel (e.g. under Admin's Master
  // tab) rather than its own standalone route, skip the page-level p-6/h1 —
  // the host page already provides that chrome. Default false preserves
  // every other page's existing standalone appearance unchanged.
  bare?: boolean;
}

const emptyForm = (fields: MasterField[]) => {
  const form: Record<string, any> = {};
  for (const f of fields) form[f.key] = f.type === 'checkbox' ? false : '';
  return form;
};

export default function MasterCrudPage({ config }: { config: MasterCrudConfig }) {
  const hasCodeName = config.hasCodeName !== false;
  const allFields: MasterField[] = hasCodeName
    ? [
        { key: 'code', label: 'Code', type: 'text', maxLength: 6, required: true },
        { key: 'name', label: 'Name', type: 'text', maxLength: 50, required: true },
        ...config.fields,
      ]
    : config.fields;

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>(emptyForm(allFields));
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(config.apiBase);
      setRows(res.data[config.listKey] || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.apiBase]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm(allFields));
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: any) => {
    setEditingId(row.id);
    const form: Record<string, any> = {};
    for (const f of allFields) form[f.key] = row[toSnake(f.key)] ?? row[f.key] ?? (f.type === 'checkbox' ? false : '');
    setFormData(form);
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await api.put(`${config.apiBase}/${editingId}`, formData);
      } else {
        await api.post(config.apiBase, formData);
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${config.apiBase}/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
      setDeleteTarget(null);
    }
  };

  const columns: Column<any>[] = allFields
    .filter((f) => f.type !== 'textarea')
    .slice(0, 6)
    .map((f) => ({
      key: toSnake(f.key),
      header: f.label,
      render: (row) => {
        const v = row[toSnake(f.key)];
        if (f.type === 'checkbox') return v ? 'Yes' : 'No';
        if (f.type === 'select' && f.options) return f.options.find((o) => o.value === v)?.label || v;
        return v ?? '';
      },
    }));
  columns.push({
    key: 'actions',
    header: 'Actions',
    render: (row) => (
      <div className="flex gap-2">
        <Button variant="ghost" onClick={() => openEdit(row)}>
          Edit
        </Button>
        <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>
          Delete
        </Button>
      </div>
    ),
  });

  return (
    <div className={config.bare ? '' : 'p-6'}>
      <div className="flex items-center justify-between mb-4">
        {config.bare ? (
          <h2 className="text-lg font-semibold">{config.title}</h2>
        ) : (
          <h1 className="text-xl font-semibold text-gray-800">{config.title}</h1>
        )}
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table columns={columns} rows={rows} loading={loading} />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? `Edit ${config.title}` : `New ${config.title}`}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          {allFields.map((f) => (
            <FieldInput key={f.key} field={f} value={formData[f.key]} onChange={(v) => setFormData({ ...formData, [f.key]: v })} />
          ))}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete"
        message={`Delete "${deleteTarget?.name || deleteTarget?.code || ''}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function toSnake(key: string) {
  return key.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function FieldInput({ field, value, onChange }: { field: MasterField; value: any; onChange: (v: any) => void }) {
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {field.label}
      </label>
    );
  }
  if (field.type === 'select') {
    return (
      <Select
        label={field.label}
        options={field.options || []}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
      />
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">{field.label}</label>
        <textarea
          className="px-4 py-2 border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }
  return (
    <Input
      label={field.label}
      type={field.type === 'number' ? 'number' : 'text'}
      maxLength={field.maxLength}
      value={value ?? ''}
      onChange={(e) => onChange(field.type === 'number' ? e.target.value : e.target.value)}
      required={field.required}
    />
  );
}
